import React, { useState, useEffect } from "react";
import {
  CATALOG_CATEGORIES,
  AVAILABLE_CATALOG_IMAGES
} from "../../constants/catalogData";

export default function CatalogManagerModal({
  isOpen,
  onClose,
  products,
  onSaveProduct,
  onDeleteProduct,
  onToggleFeatured,
  onResetDefaults,
  showResetDefaults = true,
  editingProduct,
  setEditingProduct
}) {
  const [activeTab, setActiveTab] = useState("list"); // 'list' or 'form'
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Form State
  const initialFormState = {
    id: "",
    name: "",
    tag: "",
    badge: "DESTACADO",
    pills: ["15g proteína", "Bajo en azúcar"],
    category: "Proteína & Shakes",
    rating: 5,
    isFeatured: true,
    img: "/catalog/shake.jpg",
    location: "Máquinas Central Campus Universitario",
    shortDesc: "",
    description: "",
    nutrition: {
      calories: "180 kcal",
      protein: "15g",
      carbs: "14g",
      fats: "4g",
      sugars: "2g",
      fiber: "3g",
      sodium: "110mg"
    },
    ingredients: "Ingredientes naturales seleccionados para nutrición limpia.",
    allergens: "Sin gluten. Apto para celíacos.",
    machines: "Máquinas Central Campus Universitario, Torre Médica Bella Vista",
    spiral: "A1"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (editingProduct) {
      setFormData({
        ...editingProduct,
        machines: Array.isArray(editingProduct.machines)
          ? editingProduct.machines.join(", ")
          : editingProduct.machines || "",
        nutrition: {
          ...initialFormState.nutrition,
          ...(editingProduct.nutrition || {})
        }
      });
      setActiveTab("form");
    }
  }, [editingProduct]);

  if (!isOpen) return null;

  const handleOpenNewForm = () => {
    setEditingProduct(null);
    setFormData({
      ...initialFormState,
      id: "prod-" + Date.now()
    });
    setFormErrors({});
    setActiveTab("form");
  };

  const handleEditClick = (p) => {
    setEditingProduct(p);
  };

  const handleFormChange = (field, val) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleNutritionChange = (macro, val) => {
    setFormData((prev) => ({
      ...prev,
      nutrition: {
        ...prev.nutrition,
        [macro]: val
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormErrors({ name: "El nombre es obligatorio" });
      return;
    }

    const processedMachines = typeof formData.machines === "string"
      ? formData.machines.split(",").map((s) => s.trim()).filter(Boolean)
      : formData.machines;

    const finalProduct = {
      ...formData,
      id: formData.id || "prod-" + Date.now(),
      rating: Math.min(5, Math.max(0, Number(formData.rating) || 0)),
      location: formData.location || (processedMachines[0] || "Máquinas VittaLink"),
      machines: processedMachines.length > 0 ? processedMachines : ["Todas las máquinas VittaLink"],
      spiral: formData.spiral || "A1",
      updatedAt: new Date().toISOString()
    };

    onSaveProduct(finalProduct);
    setEditingProduct(null);
    setActiveTab("list");
  };

  const filteredProducts = products.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.tag && p.tag.toLowerCase().includes(search.toLowerCase())) ||
      (p.spiral && p.spiral.toLowerCase().includes(search.toLowerCase()));
    const matchCat =
      selectedCategory === "all" || p.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const featuredCount = products.filter((p) => p.isFeatured).length;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2100,
        backgroundColor: "rgba(15, 35, 23, 0.65)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px"
      }}
      onClick={onClose}
    >
      <style>{`
        .mgr-scroll::-webkit-scrollbar { width: 6px; }
        .mgr-scroll::-webkit-scrollbar-track { background: #f1f5f2; }
        .mgr-scroll::-webkit-scrollbar-thumb { background: #86efac; border-radius: 99px; }
        .mgr-scroll::-webkit-scrollbar-thumb:hover { background: #22c55e; }
        .mgr-input {
          width: 100%;
          background: #f8faf8;
          border: 1px solid #e2ece3;
          border-radius: 12px;
          padding: 10px 14px;
          color: #0f2317;
          font-family: inherit;
          font-size: 13.5px;
          outline: none;
          transition: border-color .2s;
        }
        .mgr-input:focus {
          border-color: #16a34a;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.12);
        }
        .mgr-btn {
          padding: 10px 20px;
          border-radius: 9999px;
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
      `}</style>

      <div
        className="mgr-scroll"
        style={{
          position: "relative",
          width: "min(1080px, 98%)",
          maxHeight: "92vh",
          overflowY: "auto",
          backgroundColor: "#ffffff",
          borderRadius: 24,
          border: "1px solid #e2ece3",
          boxShadow: "0 25px 65px rgba(16, 50, 28, 0.18)",
          color: "#0f2317",
          padding: "28px"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #eef3ee",
            paddingBottom: "18px",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                backgroundColor: "#dcfce7",
                color: "#166534",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: "20px"
              }}
            >
              ⚙️
            </div>
            <div>
              <h2 style={{ fontSize: "20px", fontWeight: 800, color: "#0f291e", margin: 0 }}>
                Manager de Catálogo VittaLink
              </h2>
              <p style={{ fontSize: "12.5px", color: "#64748b", margin: "2px 0 0 0" }}>
                Administra los productos de las máquinas expendedoras y define cuáles se muestran como <strong>Destacados</strong>.
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                border: "1px solid #bbf7d0",
                padding: "6px 14px",
                borderRadius: 9999,
                fontSize: "12px",
                fontWeight: 700
              }}
            >
              ★ {featuredCount} Destacados
            </span>

            <button
              onClick={onClose}
              style={{
                background: "#f1f5f2",
                border: "1px solid #e2ece3",
                color: "#1e3a29",
                width: 38,
                height: 38,
                borderRadius: "50%",
                cursor: "pointer",
                display: "grid",
                placeItems: "center",
                fontSize: "16px"
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tab Controls */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setActiveTab("list")}
              className="mgr-btn"
              style={{
                backgroundColor: activeTab === "list" ? "#166534" : "#f1f5f2",
                color: activeTab === "list" ? "#ffffff" : "#475569"
              }}
            >
              📋 Ver Lista ({products.length})
            </button>
            <button
              onClick={handleOpenNewForm}
              className="mgr-btn"
              style={{
                backgroundColor: activeTab === "form" && !editingProduct ? "#166534" : "#f1f5f2",
                color: activeTab === "form" && !editingProduct ? "#ffffff" : "#475569"
              }}
            >
              ➕ {editingProduct ? "Editar Producto" : "Nuevo Producto"}
            </button>
          </div>

          {activeTab === "list" && (
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Buscar por nombre, tag..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mgr-input"
                style={{ width: 220, padding: "8px 12px" }}
              />

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="mgr-input"
                style={{ width: 170, padding: "8px 12px" }}
              >
                <option value="all">Todas las Categorías</option>
                {CATALOG_CATEGORIES.filter((c) => c !== "Todos" && c !== "Destacados").map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              {showResetDefaults && <button
                type="button"
                onClick={onResetDefaults}
                className="mgr-btn"
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#b91c1c",
                  border: "1px solid #fecaca"
                }}
                title="Restaura la lista original de productos"
              >
                ↺ Restaurar
              </button>}
            </div>
          )}
        </div>

        {/* TAB 1: PRODUCT LIST */}
        {activeTab === "list" && (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0 8px",
                fontSize: "13px"
              }}
            >
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left" }}>
                  <th style={{ padding: "8px 12px" }}>Producto</th>
                  <th style={{ padding: "8px 12px" }}>Categoría</th>
                  <th style={{ padding: "8px 12px" }}>Valoración</th>
                  <th style={{ padding: "8px 12px", textAlign: "center" }}>★ Destacado</th>
                  <th style={{ padding: "8px 12px" }}>Espiral</th>
                  <th style={{ padding: "8px 12px", textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                      No se encontraron productos coincidentes.
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((p) => (
                    <tr
                      key={p.id}
                      style={{
                        backgroundColor: "#f8faf8",
                        borderRadius: 12,
                        transition: "background 0.2s"
                      }}
                    >
                      {/* Product Thumbnail + Name */}
                      <td style={{ padding: "12px", borderRadius: "12px 0 0 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <img
                            src={p.img}
                            alt={p.name}
                            style={{
                              width: 44,
                              height: 44,
                              borderRadius: 10,
                              objectFit: "cover",
                              border: "1px solid #e2ece3"
                            }}
                            onError={(e) => {
                              e.target.src = "/catalog/hero.jpg";
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: 700, color: "#0f291e" }}>{p.name}</div>
                            <span
                              style={{
                                fontSize: "11px",
                                color: "#166534",
                                backgroundColor: "#dcfce7",
                                padding: "2px 8px",
                                borderRadius: 999
                              }}
                            >
                              {p.tag}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td style={{ padding: "12px", color: "#475569" }}>
                        {p.category}
                      </td>

                      {/* Master-managed rating */}
                      <td style={{ padding: "12px", fontWeight: 800, color: "#166534", whiteSpace: "nowrap" }}>
                        <span aria-label={`${Number(p.rating ?? 5).toFixed(1)} de 5 estrellas`} style={{ color: "#eab308", letterSpacing: 1 }}>{Array.from({ length: 5 }, (_, i) => i < Math.round(Number(p.rating ?? 5)) ? "★" : "☆").join("")}</span>
                        <span style={{ color: "#166534", marginLeft: 6 }}>{Number(p.rating ?? 5).toFixed(1)}</span>
                      </td>

                      {/* Quick Featured Toggle */}
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          type="button"
                          onClick={() => onToggleFeatured(p.id)}
                          style={{
                            backgroundColor: p.isFeatured ? "#dcfce7" : "#f1f5f2",
                            border: `1px solid ${p.isFeatured ? "#86efac" : "#e2ece3"}`,
                            color: p.isFeatured ? "#166534" : "#64748b",
                            padding: "6px 12px",
                            borderRadius: 9999,
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px"
                          }}
                          title="Hacer clic para activar o desactivar como Destacado"
                        >
                          <span>{p.isFeatured ? "★" : "☆"}</span>
                          {p.isFeatured ? "Destacado" : "Normal"}
                        </button>
                      </td>

                      {/* Spiral code */}
                      <td style={{ padding: "12px", color: "#64748b" }}>
                        {p.spiral || "A1"}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "12px", textAlign: "right", borderRadius: "0 12px 12px 0" }}>
                        <div style={{ display: "inline-flex", gap: "8px" }}>
                          <button
                            type="button"
                            onClick={() => handleEditClick(p)}
                            style={{
                              backgroundColor: "#ffffff",
                              border: "1px solid #e2ece3",
                              color: "#166534",
                              padding: "6px 12px",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 700
                            }}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteProduct(p.id)}
                            style={{
                              backgroundColor: "#fee2e2",
                              border: "1px solid #fecaca",
                              color: "#b91c1c",
                              padding: "6px 12px",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 700
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* TAB 2: PRODUCT EDIT / CREATE FORM */}
        {activeTab === "form" && (
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ fontSize: "16px", color: "#166534", margin: 0, fontWeight: 800 }}>
                {editingProduct ? `Editando: ${editingProduct.name}` : "Añadir Nuevo Producto al Catálogo"}
              </h3>
              <button
                type="button"
                onClick={() => setActiveTab("list")}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#64748b",
                  fontSize: "13px",
                  cursor: "pointer"
                }}
              >
                ← Cancelar y volver a la lista
              </button>
            </div>

            {/* Basic Info Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Nombre del Producto *
                </label>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="Ej: Shake Vainilla"
                  required
                />
                {formErrors.name && (
                  <span style={{ fontSize: "11px", color: "#dc2626" }}>{formErrors.name}</span>
                )}
              </div>

              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Categoría
                </label>
                <select
                  className="mgr-input"
                  value={formData.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                >
                  {CATALOG_CATEGORIES.filter((c) => c !== "Todos" && c !== "Destacados").map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Valoración del producto (0 a 5 estrellas) *
                </label>
                <input
                  type="number"
                  className="mgr-input"
                  value={formData.rating ?? 5}
                  onChange={(e) => handleFormChange("rating", e.target.value)}
                  placeholder="5"
                  min="0"
                  max="5"
                  step="0.1"
                  required
                />
                <small style={{ display: "block", marginTop: 5, color: "#64748b" }}>Esta puntuación aparecerá junto a las estrellas en el catálogo.</small>
              </div>

              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Etiqueta Superior (Badge)
                </label>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.badge || ""}
                  onChange={(e) => handleFormChange("badge", e.target.value)}
                  placeholder="Ej: DESTACADO, MÁS VENDIDO, 100% NATURAL"
                />
              </div>
            </div>

            {/* Featured Switch & Spiral */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                alignItems: "center",
                padding: "16px",
                backgroundColor: "#f0fdf4",
                borderRadius: 16,
                border: "1px solid #bbf7d0"
              }}
            >
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => handleFormChange("isFeatured", e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: "#16a34a" }}
                />
                <span style={{ fontSize: "13.5px", fontWeight: 800, color: "#14532d" }}>
                  ★ Destacar este producto en las máquinas (Aparece en sección destacada)
                </span>
              </label>

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>Código Espiral:</label>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.spiral}
                  onChange={(e) => handleFormChange("spiral", e.target.value)}
                  placeholder="A1, B2..."
                  style={{ width: 80 }}
                />
              </div>
            </div>

            {/* Image Selector & Preview */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: "16px", alignItems: "center" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  Imagen del Producto
                </label>
                <select
                  className="mgr-input"
                  value={formData.img}
                  onChange={(e) => handleFormChange("img", e.target.value)}
                  style={{ marginBottom: "8px" }}
                >
                  {AVAILABLE_CATALOG_IMAGES.map((img) => (
                    <option key={img.value} value={img.value}>
                      {img.label} ({img.value})
                    </option>
                  ))}
                  <option value="custom">-- Otra URL personalizada --</option>
                </select>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.img}
                  onChange={(e) => handleFormChange("img", e.target.value)}
                  placeholder="/catalog/shake.jpg o https://..."
                />
              </div>

              {/* Preview Thumbnail */}
              <div style={{ textAlign: "center" }}>
                <span style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: 4 }}>
                  Vista Previa
                </span>
                <img
                  src={formData.img}
                  alt="preview"
                  style={{
                    width: 90,
                    height: 90,
                    borderRadius: 14,
                    objectFit: "cover",
                    margin: "0 auto",
                    border: "2px solid #bbf7d0"
                  }}
                  onError={(e) => {
                    e.target.src = "/catalog/hero.jpg";
                  }}
                />
              </div>
            </div>

            {/* Descriptions */}
            <div>
              <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                Descripción Breve (1-2 líneas)
              </label>
              <input
                type="text"
                className="mgr-input"
                value={formData.shortDesc || ""}
                onChange={(e) => handleFormChange("shortDesc", e.target.value)}
                placeholder="Ej: Batido proteico suave y refrescante, ideal para post-entreno..."
              />
            </div>

            <div>
              <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                Descripción Detallada
              </label>
              <textarea
                className="mgr-input"
                rows={2}
                value={formData.description || ""}
                onChange={(e) => handleFormChange("description", e.target.value)}
                placeholder="Describe los beneficios, sabor y por qué es ideal para el cliente..."
              />
            </div>

            {/* Nutritional Macros Table */}
            <div>
              <label style={{ fontSize: "12px", color: "#166534", fontWeight: 800, display: "block", marginBottom: 8, textTransform: "uppercase" }}>
                📊 Macronutrientes Nutricionales
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "10px" }}>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Calorías</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.calories || ""}
                    onChange={(e) => handleNutritionChange("calories", e.target.value)}
                    placeholder="180 kcal"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Proteínas</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.protein || ""}
                    onChange={(e) => handleNutritionChange("protein", e.target.value)}
                    placeholder="15g"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Carbohidratos</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.carbs || ""}
                    onChange={(e) => handleNutritionChange("carbs", e.target.value)}
                    placeholder="12g"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Grasas Sal.</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.fats || ""}
                    onChange={(e) => handleNutritionChange("fats", e.target.value)}
                    placeholder="3g"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Azúcares</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.sugars || ""}
                    onChange={(e) => handleNutritionChange("sugars", e.target.value)}
                    placeholder="2g"
                  />
                </div>
                <div>
                  <label style={{ fontSize: "11px", color: "#64748b" }}>Fibra</label>
                  <input
                    type="text"
                    className="mgr-input"
                    value={formData.nutrition?.fiber || ""}
                    onChange={(e) => handleNutritionChange("fiber", e.target.value)}
                    placeholder="3g"
                  />
                </div>
              </div>
            </div>

            {/* Ingredients & Allergens */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  🌿 Ingredientes
                </label>
                <textarea
                  className="mgr-input"
                  rows={2}
                  value={formData.ingredients || ""}
                  onChange={(e) => handleFormChange("ingredients", e.target.value)}
                  placeholder="Lista de ingredientes..."
                />
              </div>

              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  ⚠️ Alérgenos y Advertencias
                </label>
                <textarea
                  className="mgr-input"
                  rows={2}
                  value={formData.allergens || ""}
                  onChange={(e) => handleFormChange("allergens", e.target.value)}
                  placeholder="Contiene frutos secos, sin lactosa..."
                />
              </div>
            </div>

            {/* Location & Machines */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  📍 Ubicación Principal de Máquina
                </label>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.location || ""}
                  onChange={(e) => handleFormChange("location", e.target.value)}
                  placeholder="Ej: Máquinas Central Campus Universitario"
                />
              </div>

              <div>
                <label style={{ fontSize: "12px", color: "#475569", display: "block", marginBottom: 6, fontWeight: 600 }}>
                  🏢 Otras Máquinas Disponibles (separadas por coma)
                </label>
                <input
                  type="text"
                  className="mgr-input"
                  value={formData.machines}
                  onChange={(e) => handleFormChange("machines", e.target.value)}
                  placeholder="Máquinas Central Campus, Torre Médica Bella Vista..."
                />
              </div>
            </div>

            {/* Submit & Cancel Buttons */}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "10px" }}>
              <button
                type="button"
                onClick={() => setActiveTab("list")}
                className="mgr-btn"
                style={{
                  backgroundColor: "#f1f5f2",
                  color: "#475569"
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="mgr-btn"
                style={{
                  backgroundColor: "#166534",
                  color: "#ffffff",
                  padding: "12px 28px",
                  fontSize: "14px",
                  boxShadow: "0 4px 14px rgba(22, 101, 52, 0.25)"
                }}
              >
                💾 Guardar Producto
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
